/*
    Copyright (C) Paul B. Lucas 2025, 2026  ALL RIGHTS RESERVED
    Licensed to the University of Illinois Urbana Champaign

    This is a Javascript composable that uses the employees API endpoint to fetch and store employees
    from/to the database.   Three methods are incuded:

        fetchEmployeesOnOrgs -      selects employees based on a list of provided org codes
        fetchEmployeessOnQuery -    selects employees based on a generalized query array of 
                                    key:value pairs.
        saveEmployee -              saves a single employee to database
    

    The fetchs are async functions that awaits a results of an axios call to the RWA API. When promise 
    is resolved, the results are returned in agreements ref.

    API configuration data is stored as JSON file in src/configuration/ and imported as a JSON array.  Import
    can do this from a basic JSON file.  Parameters in the config array include the base-url, the POSTQL ccf
    endpoint, and authentication data for the axios headers (API key).  Details:

        {
            "apim-key-name":    "Bearer",
            "apim-key":         "--- some key ---",
            "base-url":         "https://api.elam.fs.illinois.edu",
            "ccfURL":           "https://api.elam.fs.illinois.edu/ccf",
            "agreement-path":   "/agreements",
            "employee-path":    "/employees",
            "table-path":       "/tables",
            "assets-path":      "/assets",
            "nans":             "/nans",
            "image-url":        "/https://images.elam.fs.illinois.edu/employees/"     
        }     
        
    If the API URL changes in DNS, this file must be modified.

    <Revision History>
        02/25/2025      pblucas     created  
        03/16/2026      pblucas     Corrected typos & updated copyright
        
    </Revision History>
*/

import { ref }      from 'vue'
import axios        from 'axios'
import amApiConfigs from '@/configuration/amAPI.config.json'

const useEmployeesAPI = () =>    {

    // DO NOT USE DEFAULT FOR AUTH IF AXIOS IS ACCESSING MORE THAN ONE API

    const baseURL  = amApiConfigs['base-url']
    // set specific endpoint for employees
    const URIEmployeePath   =  amApiConfigs['employee-path']+'/'
    const URIQS             =  "?v=2"
    const URIOrgs           =  'home-org/'
    
    //use the POSTQL endpoint 'ccf' for storing
    const ccfURL            =   amApiConfigs['ccfURL']
    
    // RETURNED REFERENCE VALUES
    // returned employees: array
    const emplResults      =   ref([])           
    // returned data on where data was stored (includes pkey, assigned uuid, &cta)
    const emplKeyData      =   ref([])
    // returned mapped employee from claims (template)
    const mappedEmplTemplate    =   ref({})

    // MARK: - FUNCTION TO FETCH EMPLOYEES ON GENERAL QUERY
    // Arguments:
    //      aQuery, array of key value pairs
    // Returns:
    //      sets employees ref
    //
    const  fetchEmployeesOnQuery = async (aQuery)  =>  {

        // convert array of key:value pairs to / delimited components for  URL
        // e.g.,  ["first-name: "john", "last-name":"Doe"]  =>  /first-name/John/last-name/Doe
        let uri = Object.keys(aQuery).map((k) => {
            return encodeURIComponent(k) + '/' + encodeURIComponent(aQuery[k]);
        }).join('/')
          
        // set specific endpoint for employees 
        let url =  baseURL + URIEmployeePath + uri + URIQS
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {      
            emplResults.value   =   response.data.results   
            
            // fixup composite object used in office selection
            emplResults.value.forEach( (e)  =>  {
                e['office-building']    =   {
                    'name':     e['office-building-name'],
                    'number':   e['office-building-number']
                }
            })
            
        })
        .catch( error =>  {
            console.log("FetchEmployees:: "+ error.message)
        })
 
    }   // end function

    const  deleteEmployeesOnQuery = async (aQuery)  =>  {

        // convert array of key:value pairs to / delimited components for  URL
        // e.g.,  ["first-name: "john", "last-name":"Doe"]  =>  /first-name/John/last-name/Doe
        let uri = Object.keys(aQuery).map((k) => {
            return encodeURIComponent(k) + '/' + encodeURIComponent(aQuery[k]);
        }).join('/')
          
        // set specific endpoint for employees 
        let url =  baseURL + URIEmployeePath + uri
        await axios({
            method:         'delete',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {
          emplKeyData.value   =   response.data.response    
        })
        .catch( error =>  {
            console.log("DeleteEmployees:: "+ error.message)
        })
 
    }   // end function


    // MARK: - FUNCTION TO FETCH EMPLOYEES WHOSE HOME ORG IS A MEMBER OF ORG LIST
    // Arguments:
    //      aOrgs, comma delimited list of strings representing org codes (3 or 6 digits)
    // Returns:
    //      sets employees ref
    //
    const  fetchEmployeesOnOrgs = async (aOrgs)  =>  {
       
        let employees = []
        
        let resolvedPromises = await Promise.allSettled(aOrgs.map(org => {
            let url =  baseURL + URIEmployeePath + URIOrgs +org + URIQS
            let data = axios({
                method:         'get',
                url:            url,
                headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            return data;
            }
        ))
        // have an array of settled promised with either resolve or response callbacks
        resolvedPromises.forEach((rp, num)  =>  {
            if(rp.status == 'rejected') {
                //throw Error ("Axios promise rejected: fetching orgcode: "+aOrgs[num])
            }
            else if(rp.status == 'fulfilled')  {
                // data available on promise
                const data = rp.value.data      
                //console.log("Returned promise data: "+JSON.stringify(data))

                // do some more detailed error checking on API return
                // data is JSON
                /*
                if(!(data.response['http_return'] == 202) || (data.response['http_return'] == 200)) { 

                    console.log("FetchEmployees:: error thrown in response")
                    //throw Error ("Unable to fetch employee data")
                }
                if(data.results.length == 0) { 
                    console.log("FetchEmployees:: no results on org: ")
                    //throw Error ("No employees found on orgcode "+ aOrgs[num])
                }
                    */
                // completed and valid query returns
                for (let k in data.results)  {
                    
                    // get employee object from API call returned results[]
                    let obj = data.results[k]

                    // create the composite object - was deconstruted for storage
                    //obj['office-building'] =    {
                    //    'name':     obj['office-building-name'],
                    //    'number':   obj['office-building-number']
                    //}
                    employees.push(obj)
                }
                //console.log("useEmployeesAPI: fetchOnOrgs: employees: "+JSON.stringify(employees))
                emplResults.value = employees
            }
        })
    }   // end function


    // MARK: - FUNCTION TO STORE EMPLOYEE OBJECT(S)
    // Arguments:
    //      employee, must have netid and UIN
    //      employee may be array of employees
    // Returns:
    //      sets employees ref
    //
    // Uses:  API ccf endpoint as POSTQL 
    //
    const storeEmployee = async (employee) => {

        // fixup image filename as API may be set to to URL reporting - do not store image as URL
        // class instance will on construction create the proper image
        // recreate image from UIN and last 12 digit section of uuid
        let image = ""
        
        // overwrite proper image label
        employee.image = image
        // need to generate attributes data - JSON array[employee objects]
        // NOTE: constructed object 'office-building' will be ignored on API store (no dictionary entry)
        const payload = JSON.stringify(employee)
        
        var formData = new FormData()
        formData.append('collection','employees')

        // we will update the employees on a null query string using an POST method
        // and rely on the UPDATE ON DUPLICATE KEY (UUID) in the storeEmployees() routine
        // 
        formData.append('action','POST')
        // so the query will be null - all action is in the attribute data
        formData.append('query',"{}")
        formData.append('attributes',payload)
        formData.append('parameters',"{}")

        try { 
            await axios({
                method: 'POST',
                url: ccfURL,
                data: formData,
                headers: {'Content-Type': 'multipart/form-data',
                          'Authorization': `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            .then((response) => {
                if((response.data.response['status'] == "failed") || (response.data.response['http_return'] != 200)) 
                    {   
                        throw Error ( "failed to update employee(s)")
                    }
                // results[] (ARRAY) contains PK, UUID and concatenated name as {'pkey','message', 'uuid','name-concat'}
                // can test for message.includes('success')
                // results is an array.  When storing a single employee test for emplKeyData.value[0]
                emplKeyData.value   =   response.data
                //console.log("useEmployeesAPI:: employee stored on pkey: "+emplKeyData.value[0].pkey)
            })
        }
        catch(error) {     
            console.log("storeEmployees:: Problem saving/updating employees. Message: "+error.message)   
        }        
    }   // end function

 
    // MARK: - FUNCTION TO DELETE AN EMPLOYEE CONTACT
    // Arguments:
    //      employee, comma delimited list of strings representing org codes (3 or 6 digits)
    //      index,  the index of the contact in the contacts array to delete
    // Returns:
    //      none
    //
    // Uses:  API ccf endpoint as POSTQL 
    //
    const deleteEmployeeContact = async (user, index) => {

        // fetch the employee on UUID from the employee argument
        //console.log("initial UUID fetch: "+URIEmployeePath)

        let employee        =  {}
        let url = baseURL + URIEmployeePath + 'uuid/'+ user.uuid
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })        
        .then ( (response) => {

            //console.log("Fetch employee on UUID promise resolved")
            // UUIDs are unique so take first result
            if( response.data.results.length == 0) return;
            employee   =   response.data.results[0] 

            let contactToDelete =   employee.contacts[index]  
            let sQuery = '{"uuid": "'+ employee.uuid+'"}'
            let payload = '{"contacts":{"remove":{"select":{"uuid": "'+contactToDelete.uuid+'"}}}}'

            var formData = new FormData()
            formData.append('collection','employees')
            formData.append('action','PUT')
            formData.append('query', sQuery)
            formData.append('attributes', payload)
            formData.append('parameters',"{}") 

            axios({
                    method: 'POST',
                    url: ccfURL,
                    data: formData,
                    headers: {'Content-Type': 'multipart/form-data',
                    'Authorization': `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })  
            .catch( error =>  {
                console.log("DeleteEmployeeContact:: "+ error.message)
            })
            
        })
        .catch( (e)  => {
            console.log("DeleteEmployeeContact:: Fetch employee on UUID "+ e.message)
        }) 
    }   // end function

    // FUNCITON:  Parses claims to created a template employee from available claims.
    //            Not all properties are availale.  Fallback if Banner unavailable.
    //            Admins must fill in missing data on employee record
    // Arguments: claims    claims object from authentication
    //
    // Returns:   emplTemplate  employee object with all claims data added  
    //            sets mappedEmplTemplate ref

    const mapTemplatedEmployee = (user) =>    {
        // extended claims
        const claims = user.idTokenClaims
        // generate netid from 'username' in claims
        let netid = user.username.split("@")[0]
        let college = (claims.hasOwnProperty('department'))?claims.department:""

        // parse out the claim 'name' into components for use on templates
        // first and last are available on claims as givenName and surname
        // middle initial must be parsed from claims.name
        let sNames      =   user.name.replace(',','')  // Format: Last, First M
        let aNames      =   sNames.split(' ')       
        let nameLast    =   ""
        let nameFirst   =   ""
        let nameMI      =   ""
   
        switch(aNames.length) {
            case 1:
                // single name (Indonesian) make it last
                nameLast    =   aNames[0]
                nameFirst   =   ""
                nameMI      =   ""
                break
            case 2:
                // John Doe
                nameLast    =   aNames[0]
                nameFirst   =   aNames[1]
                nameMI      =   ""
                break
            case 3:
                // John Q Doe
                nameLast    =   aNames[0]
                nameFirst   =   aNames[1]
                nameMI      =   aNames[2]
                break
            case 4:
                nameLast    =   aNames[0]+" "+aNames[1]
                nameFirst   =   aNames[2]
                nameMI      =   aNames[3]
                break
        }

        let templatedEmployee = {
            'name-concat':            user.name,
            'first-name':             nameFirst,
            'last-name':              nameLast,
            'middle-initial':         nameMI,
            'netid':                  netid,
            'ms-object-id':           claims.oid,
            'uin':                    claims.uin,
            'home-org':               "",
            'eclass-code':            "",
            'title':                  claims.jobtitle,
            'address1':               "",
            "address2":               "",
            "city":                   "",
            "state":                  "Illinois",
            "postcode":               "",
            "country":                "United States",
            "mailcode":               "",
            "timezone":               "America/Chicago",        // default cst/cdt
            "req-photo-suppress":     false,
            "college":                college,                  // college returned as department on claims
            "college-code":           null,
            "department":             null,
            "department-code":        null,
            "division":               null,
            'division-code':          null,
    
            // the following three user properties will be updated by membership in Entra security groups
            // these are mutated on store.state.user AFTER user is update with new Employee
            'is-sys-admin':           false,
            'is-dept-admin':          false,
            // additional security constraints
            // must be added by admin on employee record
            'admins-multiple-orgs':   false,
            'allowed-orgs':           [],  
            // pull known contacts from claims   
            'contacts':               []
        }

        // can pull email from claims so create a contact object
        let contact1 = {}
        contact1.type = "email"
        // use claims.username( email) 
        contact1.value = user.username
        // add to object
        templatedEmployee.contacts.push(contact1)
 
        // set return ref
        mappedEmplTemplate.value = templatedEmployee
        return templatedEmployee;
    }

    return {emplResults, emplKeyData, mappedEmplTemplate, fetchEmployeesOnQuery, fetchEmployeesOnOrgs, 
            storeEmployee, deleteEmployeesOnQuery, deleteEmployeeContact, 
            mapTemplatedEmployee}
}

export default useEmployeesAPI